// Workflow-stage grouping for the admin tab nav.
//
// 28 admin tabs is far past the ~7-item scannable limit for a single row.
// Grouping by workflow stage keeps the primary row at 6-7 items (1 standalone
// + 5 groups) and pushes the long list of leaf tabs into a secondary row that
// only shows the active group's children.
//
// Today is special-cased: no group, lives in the primary row alone, clicking
// it shows only Today (no secondary row).

import type { Tab } from '../components/Header'

export type GroupId = 'plan' | 'build' | 'money' | 'people' | 'marketing' | 'ops'

export type TabGroup = {
  id: GroupId
  label: string
  tabs: Array<{ id: Tab; label: string }>
}

export const TAB_GROUPS: TabGroup[] = [
  {
    id: 'plan',
    label: 'Plan',
    tabs: [
      { id: 'plans', label: 'Plans' },
      { id: 'spec', label: 'Spec Book' },
      { id: 'scope', label: 'Scope' },
      { id: 'decisions', label: 'Decisions' },
      { id: 'selections', label: 'Selections' },
    ],
  },
  {
    id: 'build',
    label: 'Build',
    tabs: [
      { id: 'schedule', label: 'Schedule' },
      { id: 'site-walks', label: 'Site Walks' },
      { id: 'daily-log', label: 'Daily Log' },
      { id: 'inspections', label: 'Inspections' },
      { id: 'materials', label: 'Materials' },
      { id: 'punch', label: 'Punch' },
    ],
  },
  {
    id: 'money',
    label: 'Money',
    tabs: [
      { id: 'budget', label: 'Budget' },
      { id: 'bids', label: 'Bids' },
      { id: 'change-orders', label: 'COs' },
      { id: 'draws', label: 'Draws' },
      { id: 'financials', label: 'Financials' },
    ],
  },
  {
    id: 'people',
    label: 'People',
    tabs: [
      { id: 'subs', label: 'Subs' },
      { id: 'comms', label: 'Comms' },
      { id: 'lots', label: 'Lots' },
      { id: 'warranty', label: 'Warranty' },
    ],
  },
  {
    id: 'marketing',
    label: 'Marketing',
    tabs: [
      { id: 'lead-inbox', label: 'Lead Inbox' },
    ],
  },
  {
    id: 'ops',
    label: 'Ops',
    tabs: [
      { id: 'inbox', label: 'Inbox' },
      { id: 'compliance', label: 'Compliance' },
      { id: 'lessons', label: 'Lessons' },
      { id: 'insights', label: 'Insights' },
      { id: 'digest', label: 'Digest' },
      { id: 'settings', label: 'Settings' },
    ],
  },
]

// Reverse index: tab id → its group id. 'today' is intentionally absent;
// callers must special-case Today (no group).
export const TAB_TO_GROUP: Partial<Record<Tab, GroupId>> = (() => {
  const m: Partial<Record<Tab, GroupId>> = {}
  for (const g of TAB_GROUPS) for (const t of g.tabs) m[t.id] = g.id
  return m
})()

// All known leaf tabs (excluding 'today') as a Set for fast validation.
export const ALL_GROUPED_TABS: Set<Tab> = new Set(
  TAB_GROUPS.flatMap((g) => g.tabs.map((t) => t.id))
)

export function getGroupForTab(tab: Tab): GroupId | null {
  if (tab === 'today') return null
  return TAB_TO_GROUP[tab] ?? null
}

export function getDefaultTabForGroup(group: GroupId): Tab {
  return TAB_GROUPS.find((g) => g.id === group)!.tabs[0].id
}
